// main.js — Vue 3 entry point with async Cornerstone bootstrap.
//
// Why the async wrapper: cornerstone3D.init() and cornerstoneDICOMImageLoader.init()
// return Promises. Mounting the Vue app before they resolve races with component
// lifecycle — viewports can mount before providers and workers are ready, which
// shows up as intermittent "first series fails to render" bugs. Await everything
// before createApp(...).mount().

import { createApp } from "vue";
import { createPinia } from "pinia";
import * as cornerstone3D from "@cornerstonejs/core";
import * as cornerstone3DTools from "@cornerstonejs/tools";
import cornerstoneDICOMImageLoader from "@cornerstonejs/dicom-image-loader";
import ContextMenu from "@imengyu/vue3-context-menu";

import App from "./App.vue";
import router from "./router";

import "material-icons/iconfont/material-icons.css";
import "@imengyu/vue3-context-menu/lib/vue3-context-menu.css";

// Reasonable worker cap. More than ~4 tends to thrash on modest CPUs without
// giving a meaningful throughput gain — the decoders are already fast.
const MAX_WEB_WORKERS = Math.min(navigator.hardwareConcurrency ?? 1, 4);

// Progressive loading: streams HTJ2K RPCL images so the first partial frame
// paints well before the full payload arrives. Safe to register unconditionally
// — it's a no-op for non-HTJ2K transfer syntaxes.
const PROGRESSIVE_CONFIG = {
  retrieveOptions: { single: { streaming: true } },
};

function registerProgressiveLoading() {
  const { imageRetrieveMetadataProvider } = cornerstone3D.utilities ?? {};
  if (!imageRetrieveMetadataProvider?.add) {
    // Older @cornerstonejs/core versions don't ship this provider. Safe to skip —
    // the app still works, just without progressive loading.
    console.info("[cornerstone] imageRetrieveMetadataProvider unavailable; progressive loading disabled");
    return;
  }
  imageRetrieveMetadataProvider.add("stack", PROGRESSIVE_CONFIG);
  imageRetrieveMetadataProvider.add("volume", PROGRESSIVE_CONFIG);
}

async function bootstrap() {
  // Order matters: core first (sets up metadata providers + rendering engine),
  // then tools (depends on core), then image loader (registers wadors: scheme
  // with the loader registry that core owns).
  await cornerstone3D.init();
  await cornerstone3DTools.init();
  await cornerstoneDICOMImageLoader.init({ maxWebWorkers: MAX_WEB_WORKERS });

  registerProgressiveLoading();

  // ReferenceLines tool breaks with CPU rendering — leave GPU on.
  // cornerstone3D.setUseCPURendering(true);   // ← don't uncomment

  createApp(App)
    .use(createPinia())
    .use(router)
    .use(ContextMenu)
    .mount("#app");
}

bootstrap().catch((err) => {
  console.error("[bootstrap] Cornerstone initialization failed:", err);
  // Show a user-facing error rather than leaving them staring at a blank page.
  const root = document.getElementById("app");
  if (root) {
    root.textContent = "Viewer failed to start. Please refresh; if the problem persists, contact support.";
  }
});